import { useNavigate, useParams } from 'react-router-dom';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import PageForm from '../../templates/PageForm';
import PageSearch from '../../molecules/PageSearch';
import PresentationModal from '../../organisms/PresentationModal';
import SelectForm from '../../organisms/SelectForm';
import MaskEdit from '../../organisms/MaskEdit';
import SicorReportCard from '../../organisms/SicorReportCard';
import CardList from '../../organisms/CardList';
import APagination from '../../molecules/APagination';
import useFetch from '../../../hooks/useFetch';
import appConfig from '../../../config/app-config';
import { appendServiceToUrl } from '../../../utils/urlTools';

const SICOR_API_URL = appConfig.apis.sicorApiEnv.address;
const API_KEY = appConfig.apis.sicorApiEnv.key;
const BASE_URL = `${appendServiceToUrl(SICOR_API_URL, "/sicor")}/reports/page-list`;

interface ReportSummary {
    document: string;
    documentFormatted: string;
    totalReports: number;
    totalYears: number;
    reportsPerYear: { [year: string]: number }
}

const SicorReportPageList = () => {
    const navigate = useNavigate();
    const { page = '0', pageSize = '20', field: fieldSorted = 'document', order: orderSorted = 'ASC', filter }: any = useParams();

    const showModal = useSignal<boolean>(false);
    const documentType = useSignal<'CPF' | 'CNPJ'>('CPF');
    const documentValue = useSignal('');

    let document = '';
    if (filter) {
        [document = ''] = filter.match(/=(.*?);/g)?.map((m: string) => m.slice(1, -1)) ?? [''];
    }

    const url = `${BASE_URL}?page=${page}&pageSize=${pageSize}&order=${fieldSorted},${orderSorted}&document=${document}`;
    const { isLoading, data, error, isTokenError } = useFetch(url, [{ key: 'x-api-key', value: API_KEY }]);

    if (isTokenError) navigate('/denied');
    if (error) navigate('/network-error');

    const pageChanged = (p: number) => {
        navigate(`/sicor/report/${p - 1}/${pageSize}/${fieldSorted}/${orderSorted}${filter ? `/${filter}` : ''}`, { replace: true });
    };

    const documentChanged = (value: string) => {
        navigate(`/sicor/report/${page}/${pageSize}/${fieldSorted}/${orderSorted}/d=${value};`, { replace: true });
    };

    const handleProcess = async () => {
        const token = localStorage.getItem('authToken');
        try {
            const response = await fetch(`${SICOR_API_URL}/sicor/reports`, {
                method: 'POST',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    documentType: documentType.value === 'CPF' ? 'socialId' : 'govTaxId',
                    document: documentValue.value.replace(/\D/g, ''),
                }),
            });

            if (response.ok) {
                toast.info('Processamento iniciado! Os relatórios serão exibidos conforme forem processados.');
                showModal.value = false;
                documentChanged(documentValue.value.replace(/\D/g, ''));
            } else if (response.status === 401) {
                toast.error('A sessão expirou. Faça login novamente.');
                navigate('/login');
            } else {
                toast.error('Não foi possível iniciar o processamento.');
            }
        } catch {
            toast.error('Erro de comunicação com o servidor.');
        }
    };

    const renderCard = (item: ReportSummary, index: number) => (
        <SicorReportCard
            item={item}
            onClick={() => navigate(`/sicor/document/${item.document}/0/20/bank/ASC`)}
        />
    );

    const items: ReportSummary[] = data?.list || [];

    return (
        <PageForm
            pageTitle="Relatórios Sicor"
            apiVersion="v1"
            isLoading={isLoading}
            breadcrumb={[{ label: 'Relatórios Sicor', url: '#' }]}
            controlSlot={
                <div className="flex justify-between items-center">
                    <PageSearch
                        value={document}
                        label="Procurar por..."
                        onSearch={documentChanged}
                        filterCleanVisible={true}
                        filterCleanEnabled={Boolean(document)}
                        onFilterClean={() => documentChanged('')}
                        configureVisible={false}
                        className="flex-1 mr-4"
                    />
                    <button
                        type="button"
                        className="text-white bg-blue-500 hover:bg-blue-700 font-medium rounded text-sm px-4 py-2"
                        onClick={() => {
                            documentValue.value = '';
                            documentType.value = 'CPF';
                            showModal.value = true;
                        }}
                    >
                        Novo relatório
                    </button>
                </div>
            }
        >
            <div className="space-y-4">
                <CardList
                    items={items}
                    renderCard={renderCard}
                    columns={3}
                    emptyMessage="Nenhum relatório encontrado..."
                />

                {data && (
                    <APagination
                        currentPage={parseInt(page) + 1}
                        totalPages={data.totalPages || 1}
                        onPageChange={pageChanged}
                    />
                )}
            </div>

            <PresentationModal
                id="sicor-report-modal"
                show={showModal.value}
                onClose={() => (showModal.value = false)}
                header="Novo relatório"
                footer={(
                    <div className="flex justify-end gap-2">
                        <button
                            onClick={() => (showModal.value = false)}
                            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleProcess}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            Processar...
                        </button>
                    </div>
                )}
            >
                <div className="p-4">
                    <div className="mb-2 text-sm text-blue-800 rounded-lg bg-blue-50 p-4" role="alert">
                        O processamento é demorado e o sistema irá apresentar os relatórios conforme forem sendo processados.
                    </div>
                    <div className="grid grid-cols-2 gap-6 items-start">
                        <SelectForm
                            id="document-type"
                            label="Tipo do documento"
                            value={documentType.value}
                            onChange={(v) => {
                                documentType.value = v as 'CPF' | 'CNPJ';
                                documentValue.value = '';
                            }}
                            options={[
                                { value: 'CPF', label: 'CPF' },
                                { value: 'CNPJ', label: 'CNPJ' },
                            ]}
                            className="w-full"
                        />
                        <MaskEdit
                            id="document-value"
                            label={documentType.value}
                            mask={documentType.value === 'CPF' ? '___.___.___-__' : '__.___.___/____-__'}
                            replacement={{ '_': /\d/ }}
                            value={documentValue.value}
                            onChange={(e) => (documentValue.value = e.target.value)}
                            className="w-full"
                        />
                    </div>
                </div>
            </PresentationModal>
        </PageForm>
    );
};

export default SicorReportPageList;
